import { z } from 'zod';
import { zId } from './primitivos.js';
/**
 * Os contratos de autenticação.
 *
 * O mesmo schema valida o corpo da requisição no servidor e o
 * formulário de #login no cliente.
 *
 * O login é MULTI-ETAPA de propósito. Em multi-tenant ele não devolve
 * "logado": devolve a próxima pergunta — o segundo fator, ou qual
 * arena entrar. A sessão carrega uma membership ativa, não um usuário.
 *
 * Ver: Arena_backend/Docs/plano/02-identidade-sessao.md
 */
/**
 * Política de senha mínima e declarada.
 *
 * Deliberadamente modesta: o documento 02 registra "política de senha
 * não definida" como pendência, e um mínimo frouxo hoje é melhor que
 * um mínimo inventado que a arena vai contornar amanhã. Verificação
 * contra listas de senhas vazadas fica para quando houver decisão.
 */
export const zSenha = z.string().min(10, 'A senha precisa de ao menos 10 caracteres.').max(200);
export const zEmail = z.string().trim().toLowerCase().email('E-mail inválido.');
// ---------------------------------------------------------------------
// Esqueci a senha
// ---------------------------------------------------------------------
/**
 * O código que o Dono gera e entrega à pessoa.
 *
 * Não há envio automático no produto — nem e-mail nem WhatsApp —, e isso
 * molda o fluxo: quem esqueceu a senha fala com o Dono, que gera um
 * código de uso único na tela de Acessos e passa pelo canal que os dois já
 * usam. É menos cômodo que um link no e-mail e tem uma propriedade que
 * um link no e-mail não tem: o Dono SABE que trocou a senha de alguém, e a
 * trilha sabe que foi ele.
 *
 * O formato é o dos códigos de recuperação do 2FA — `A1B2-C3D4` em
 * alfabeto sem 0/O e 1/I —, porque ele vai ser lido ao telefone.
 */
export const TAMANHO_DO_CODIGO_DE_SENHA = 8;
export const VALIDADE_DO_CODIGO_DE_SENHA_HORAS = 24;
/**
 * O código como a pessoa digita: maiúsculas, e o hífen é opcional. Quem
 * recebeu "A1B2-C3D4" pelo telefone digita com ou sem ele, e recusar por
 * causa do hífen é atrito sem razão.
 */
export const zCodigoDeSenha = z
    .string()
    .trim()
    .toUpperCase()
    .transform((c) => c.replace(/-/g, ''))
    .refine((c) => new RegExp(`^[A-Z0-9]{${TAMANHO_DO_CODIGO_DE_SENHA}}$`).test(c), 'O código tem 8 caracteres, como "A1B2-C3D4".');
/**
 * `POST /auth/senha/redefinir` — a troca, sem sessão.
 *
 * Os três juntos: o e-mail diz de quem é a conta, o código prova que o
 * Dono autorizou, a senha nova é o que fica. Uma resposta só para todo
 * "não" — código errado, expirado, já usado, e-mail que não existe: o
 * contrário contaria a um curioso quais e-mails têm conta.
 */
export const zRedefinicaoDeSenha = z.object({
    email: zEmail,
    codigo: zCodigoDeSenha,
    novaSenha: zSenha,
});
export const zLogin = z.object({
    email: zEmail,
    senha: z.string().min(1, 'Informe a senha.'),
    /** Atalho de quem já sabe em qual arena quer entrar. */
    arenaId: zId.optional(),
});
/** Código de 6 dígitos do app autenticador, ou código de recuperação. */
export const zCodigoTotp = z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9-]{6,20}$/, 'Código inválido.');
export const zLoginTotp = z.object({
    desafio: z.string().min(1),
    codigo: zCodigoTotp,
});
export const zLoginArena = z.object({
    desafio: z.string().min(1),
    arenaId: zId,
});
export const zRefresh = z.object({ refresh: z.string().min(1) });
/**
 * Trocar de arena consome o refresh atual: a família antiga é
 * encerrada e uma nova nasce apontando para a arena escolhida. Sem o
 * refresh no corpo, a sessão anterior ficaria viva apontando para a
 * arena errada.
 */
export const zTrocarArena = z.object({ arenaId: zId, refresh: z.string().min(1) });
export const zAtivarTotp = z.object({ codigo: zCodigoTotp });
export const zDesativarTotp = z.object({
    senha: z.string().min(1),
    codigo: zCodigoTotp,
});
/**
 * Aceite de termo.
 *
 * `imagem` é separado de `uso` porque #m-aluno trata o consentimento
 * de imagem como revogável de forma independente.
 */
export const TIPOS_DE_TERMO = ['uso', 'privacidade', 'imagem'];
export const zAceiteTermo = z.object({
    tipo: z.enum(TIPOS_DE_TERMO),
    versao: z.string().min(1),
});
//# sourceMappingURL=auth.js.map